// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

/**
 * Field-name alignment for the DeviceRegistryAPIv1 response (GetDeviceBySerialNumber
 * / GetDeviceBySoftwareId).
 *
 * ProcessServiceRequest.ds writes `device_response.toMap()` straight onto
 * response_payload with NO renaming: `{ success, zohologinuserid, data: {
 * serial_number, name, software_uuid, hardware_uid, ... }, is_demo }` (Zoho
 * form field spellings; `is_demo` a sibling of `data`, ADR-008 D2). The
 * widget's contract (../contracts/requests.ts ResolveDeviceResponse /
 * ../contracts/display.ts DeviceIdentity) uses different names (serialNo,
 * softwareId, hardwareId) and a `resolution` discriminant the raw record does
 * not carry. This is the ONE place that reconciles the two — the real Zoho
 * transport is where raw ZC field names first meet the contract, mirroring
 * zoho-transport.ts being "the ONE swap point" for the transport itself.
 *
 * BY THE TIME `raw` REACHES THIS MAPPER, rendezvous.ts's `readFileObject` has
 * already unwrapped the outer `{ success, data, is_demo }` envelope down to
 * `data`'s own inner content — the SAME rule au0001-response-mapper.ts and
 * the firmware-delivery-*-mapper.ts files already rely on (`raw` there IS
 * their own inner `data` object directly, not `{ success, data }`). This
 * mapper does NOT get its own second `data` unwrap; `raw` here is
 * `{ serial_number, name, software_uuid, hardware_uid, ... }` directly.
 * `is_demo`, being a sibling of `data` rather than nested inside it, is the
 * ONE field this widget needs from OUTSIDE that inner object — readFileObject
 * merges it onto the returned object for exactly this reason (its own
 * `CARRIED_ENVELOPE_SIBLINGS`), so it is still read straight off `raw`,
 * alongside the device fields, not through a second unwrap here.
 *
 * Diagnosed on #57 (PM correction): an earlier version of this file assumed
 * `raw` was still the FULL `{ success, data, is_demo }` envelope and did its
 * own `rec["data"]` unwrap — for serialNo/name/etc. this accidentally
 * self-healed via a `device = envelopeData ?? rec` fallback (since `rec` was
 * already the inner object, the fallback resolved to `rec` and things looked
 * correct), but `is_demo` has no such fallback and was silently lost — not a
 * type/format mismatch, a genuinely absent field once readFileObject already
 * discarded it.
 */

import { toUnknownableBoolean, toUnknownableString } from "@/contracts/display";
import type { ResolveDeviceResponse } from "@/contracts/requests";

/**
 * Map the raw DeviceRegistryAPIv1 response_payload onto ResolveDeviceResponse.
 * Idempotent: a payload that already carries a `resolution` discriminant
 * (e.g. the mock transport's contract-shaped fixtures) passes through
 * unchanged.
 */
export function mapDeviceRegistryResponse(raw: unknown): ResolveDeviceResponse {
  if (typeof raw !== "object" || raw === null) {
    return { resolution: "unresolved" };
  }
  const rec = raw as Record<string, unknown>;

  if (typeof rec["resolution"] === "string") {
    return rec as unknown as ResolveDeviceResponse;
  }

  // `success: false` (e.g. device_not_found) survives here only when
  // readFileObject returned the FULL outer envelope unwrapped (its own
  // `data === null` branch) — the Device Registry's own envelope-level miss
  // signal, distinct from a bridge/transport failure (which never reaches
  // this mapper; zoho-transport.ts only calls it on outcome.ok). ADR-003 D3:
  // not-found degrades to a non-disclosing "unresolved" outcome, not a
  // generic error.
  if (rec["success"] === false) {
    return { resolution: "unresolved" };
  }

  return {
    resolution: "resolved",
    identity: {
      serialNo: toUnknownableString(rec["serial_number"]),
      name: toUnknownableString(rec["name"]),
      softwareId: toUnknownableString(rec["software_uuid"]),
      hardwareId: toUnknownableString(rec["hardware_uid"]),
      isDemo: toUnknownableBoolean(rec["is_demo"]),
    },
  };
}
